import {unwrapResult} from '@reduxjs/toolkit';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {requestOK} from 'redux-thunk-kit';
import {
  Col,
  Row,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiInput,
  UiQuestionnaireLayout,
  UiText,
} from '~/components/elements';
import {UiCard} from '~/components/elements/UiCard';
import {PAGE_URLS} from '~/constants';
import {
  AT_HOME,
  WEIGHT_LOSS_KEYS,
  WEIGHT_LOSS_ONE_QUESTIONNAIRES,
  WL_PRODUCT_SKU,
  WL_PROGRAMS,
} from '~/constants/medical-questionnaires';
import {useInput, useRequestState} from '~/hooks';
import {setMedicalUUID, setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {Questionnaire, Relation} from '~/types/models/questionnaire';
import {redirectInIframe} from '~/utils/helpers';

interface Props {}

const Questionnaire13NutritionScreen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  const [redirectLoading, setRedirectLoading] = useState(false);
  const {loading} = useRequestState(submitOrLogMdq);

  const collectedData = mdq.collectedData;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(
    i => i.key === WEIGHT_LOSS_KEYS.DIETICIAN_APPOINTMENT_PREFERENCES,
  );
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires.collectedData[WEIGHT_LOSS_KEYS.DIETICIAN_APPOINTMENT_PREFERENCES]?.data,
  );

  const rules = {};
  const {inputs, setInputs, validation} = useInput({}, {rules});

  const dayList = questionnaire?.dayOptions;

  const selectedTimes = currentAnswer?.selectedTimes || [];
  const [selectedDays, setSelectedDays] = useState<Questionnaire['dayOptions']>(
    dayList?.map(day => {
      const dayIsSelected = selectedTimes?.some((selectedDay: any) => selectedDay.day === day.value);
      return {
        ...day,
        selected: dayIsSelected,
        timeOptions: day.timeOptions.map(time => {
          const timeIsSelected = selectedTimes?.some((selectedDay: any) => {
            return selectedDay.day === day.value && selectedDay.selectedTimes.includes(time.value);
          });
          return {
            ...time,
            selected: timeIsSelected,
          };
        }),
      };
    }) as Questionnaire['dayOptions'],
  );
  const filteredSelectedDays = selectedDays?.filter(day => day.selected);

  const isDisable =
    // !inputs?.selectedOption ||
    !filteredSelectedDays?.length || filteredSelectedDays?.some(val => val.timeOptions.every(el => !el.selected));

  const handleSelectedDay = (day: any, dayIndex: number) => {
    setSelectedDays(prevDays => {
      const newDays = prevDays ? [...prevDays] : [];
      newDays[dayIndex] = {...day, selected: !day.selected};
      return newDays;
    });
  };

  const handleSelectedTime = (dayIndex: number, timeIndex: number) => {
    setSelectedDays(prevDays => {
      const newDays = prevDays ? [...prevDays] : [];
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        timeOptions: newDays[dayIndex].timeOptions.map((time: any, index: number) =>
          index === timeIndex ? {...time, selected: !time.selected} : time,
        ),
      };
      return newDays;
    });
  };

  const onClickSubmit = async () => {
    const selectedDayValues = filteredSelectedDays?.map(({value: dayValue}) => ({day: dayValue}));
    const selectedTimeValues = filteredSelectedDays?.map(({value: dayValue, dayOfWeek, timeOptions}) => ({
      day: dayValue,
      dayOfWeek,
      selectedTimes: timeOptions?.filter(time => time?.selected)?.map(({value: timeValue}) => timeValue),
    }));

    const answerData = {
      key: WEIGHT_LOSS_KEYS.DIETICIAN_APPOINTMENT_PREFERENCES,
      index: questionnaire?.index || '',
      data: {
        ...inputs,
        selectedDays: selectedDayValues,
        selectedTimes: selectedTimeValues,
      },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));

    const res = await dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'submitted',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.DIETICIAN_APPOINTMENT_PREFERENCES]: answerData,
          },
        },
      }),
    );

    if (requestOK(res, submitOrLogMdq)) {
      const result = unwrapResult(res);
      if (result.getCheckoutUrl) {
        setRedirectLoading(true);
        redirectInIframe(result.getCheckoutUrl);
      }
    }

    if (res.payload?.errMsg === 'Medical Form invalid') {
      dispatch(setMedicalUUID({uid: null}));
      history.replace(PAGE_URLS.MDQ1_HOME);
    }
  };

  const renderTimes = (value: any, dayIndex: number, isSelected?: boolean) => {
    if (isSelected) {
      return (
        <Row key={value.label} align="center" justifyContent="center" w="100%" mb={5}>
          <UiText w="20%" fontWeight={600} fontSize={16}>
            {value.label.substring(0, 3)}
          </UiText>
          <Row flexWrap={'wrap'}>
            {value.timeOptions.map((timeOption: any, timeIndex: number) => (
              <UiButton
                onClick={() => handleSelectedTime(dayIndex, timeIndex)}
                key={timeOption.label}
                title={timeOption.label}
                variant={timeOption.selected ? 'solid' : 'outline'}
                mr={2}
                mt={1}
                borderRadius="1.25rem"
                minH={9}
                fontSize="sm"
                fontWeight="600"
                color="trueBlack"
                borderColor="trueBlack"
                _hover={{}}
                bg={timeOption.selected && 'lightBaseColor'}
              />
            ))}
          </Row>
        </Row>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      // disableNext={isDisable || (inputs.selectedOption === AT_HOME && !isAtHomeFilled)}
      disableNext={isDisable || redirectLoading || loading}
      pageTitle={questionnaire?.title}
      loading={redirectLoading || loading}
    >
      <Col alignItems="center" textAlign="center" mt={12} px={2}>
        <UiText fontSize="xl" fontWeight="bold" mb={10} maxW="80%">
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="sm" maxW={80} mb={5}>
          {questionnaire?.questions?.description}
        </UiText>
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.dayQuestion}
        </UiText>
        <Row justify="center" flexWrap="wrap" w="100%">
          {selectedDays?.map((day: any, index) => (
            <UiButton
              onClick={() => handleSelectedDay(day, index)}
              key={day.label}
              title={day.label.substring(0, 3)}
              variant={day.selected ? 'solid' : 'outline'}
              mr={2}
              w={{base: '62px', md: '120px'}}
              mb={5}
              minW="48px"
              borderRadius="1.25rem"
              minH={9}
              fontSize="sm"
              fontWeight="600"
              color="trueBlack"
              borderColor="trueBlack"
              _hover={{}}
            />
          ))}
        </Row>
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.timeQuestion}
        </UiText>
        {selectedDays?.map((day, dayIndex) => renderTimes(day, dayIndex, day?.selected))}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire13NutritionScreen;
